import './close-report-screen.scss'

import { FormApi } from 'final-form'
import { PropType } from 'vue'
import Component from 'vue-class-component'
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons'

import { ConditionReport, Wemi } from '@skinsoft/types'
import { id, referenceNumber } from '@skinsoft/selectors/condition-report-selectors'
import { ConditionReportDataStore, ConditionReportTransitionStateError } from '@skinsoft/entity'
import { ConditionReportPDFTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { ConditionReportPDFGenerator } from '@skinsoft/pdf-generator'
import { DATE_FORMATS, formatCurrentDate } from '@skinsoft/utils'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import { CLOSE_CLOSE_REPORT_SCREEN_TOPIC } from '../../../busTopics'
import { router, Routes } from '../../../router'

@Component({
    props: {
        conditionReport: {},
        wemi: {},
        fieldName: {},
        form: {},
        pdfMedias: {
            type: Array as PropType<string[]>,
        },
    },
    template: require('./close-report-screen.html')
})
export class CloseReportScreen extends ReactiveVue {

    public conditionReport: ConditionReport;
    public wemi: Wemi;
    public fieldName: string;
    public form: FormApi;
    private pdfMedias: string[]

    // Icons
    private backIcon = faArrowLeft;
    private checkIcon = faCheck;

    private $conditionReportDataStore: ConditionReportDataStore;
    private $conditionReportPDFTransferHelper: ConditionReportPDFTransferHelper
    private $conditionReportPDFGenerator: ConditionReportPDFGenerator

    private closingStatus: string = "";
    private closingInProgress: boolean = false;

    private closeCloseReportScreen() {
        this.emitOnBus(CLOSE_CLOSE_REPORT_SCREEN_TOPIC)
    }

    private async handleCloseReport() {
        this.closingInProgress = true;
        this.closingStatus = this.$t('closeReportScreen.fileGeneration') as string;

        const hasCachedChanges = await this.$conditionReportDataStore.hasCachedUpdates(this.conditionReport.id)
        if(window.navigator.onLine && !hasCachedChanges) {
            this.$conditionReportPDFGenerator.generatePDF(
                this.conditionReport, 
                this.wemi, 
                async (blob) => {
                    this.closingStatus = this.$t('closeReportScreen.transferingFile') as string;
        
                    const datePrefix = formatCurrentDate(DATE_FORMATS.DD_MM_YYYY_DASH_HMS_ONE_BLOCK)
                    const inventoryNumber = referenceNumber(this.conditionReport)
                    const fileName = datePrefix ? 
                        `${inventoryNumber}-${datePrefix}.pdf` 
                        : `${datePrefix}.pdf`
                    const withName = new File([blob], fileName)
        
                    this.$conditionReportPDFTransferHelper.uploadPDFDocument("application/pdf", this.conditionReport.id, withName, true)
                        .subscribe(async (mediaId: string) => {
                            this.closingStatus = this.$t('closeReportScreen.reportClosing') as string;
                            try {
                                if(mediaId) {
                                    await this.$conditionReportDataStore.closeConditionReport(
                                        id(this.conditionReport), 
                                        mediaId
                                    )
                                }
                                else {
                                    await this.$conditionReportDataStore.closeConditionReportOffline(id(this.conditionReport))
                                }

                                // Going back to closed report screen
                                router.push({ name: Routes.CLOSE_REPORTS });
                                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                    message: this.$t('closeReportScreen.notificationContent', { referenceNumber: referenceNumber(this.conditionReport) }),
                                    variant: "success"
                                })

                            } catch (err) {
                                if(err instanceof ConditionReportTransitionStateError) {
                                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                        message: this.$t('closeReportScreen.errors.transitionState'),
                                        variant: "danger",
                                    })
                                } else {
                                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                        message: this.$t('closeReportScreen.errors.pushingPDF'),
                                        variant: "danger",
                                    })
                                }
                            }
                        })
                },
                this.$i18n.locale,
                this.pdfMedias,
                false // we don't need to filter pdf as we provide pdf medias ids in excluded ids
            )
            .catch(e => {
                const alertPayload = {
                    message: this.$t('pdfDocument.generationFailureAlert'),
                    variant: 'danger'
                };
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            })
            
        } else {
            await this.$conditionReportDataStore.closeConditionReportOffline(id(this.conditionReport))
            // Going back to closed report screen
            router.push({ name: Routes.CLOSE_REPORTS });
            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                message: this.$t('closeReportScreen.notificationContent', { referenceNumber: referenceNumber(this.conditionReport) }),
                variant: "success"
            })
        }
    }
}